/**
 * WB4 Wave 2 — Finance reversal approval post connection (requires_approval FSM).
 */

import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import {
  clearWorkbenchFinanceReversalPending,
  getWorkbenchFinanceReversalPending,
  markWorkbenchFinanceReversalPending,
} from '@shared/business-workbench/workbench-finance-reversal-pending-state';
import { resolveWorkbenchTimelineObjectRef } from '@shared/business-workbench/workbench-timeline-event';
import { postFinanceReversalAfterApproval } from './workbench-state-change-revision';
import { emitWorkbenchTimelineInvalidated } from '../../../workbench/workbench-timeline-events';
import { showWorkbenchInfo, showWorkbenchSuccess } from './workbench-notifications';

export const WORKBENCH_FINANCE_REVERSAL_POSTED_EVENT =
  'inflomatrix:workbench-finance-reversal-posted';

export interface WorkbenchFinanceReversalPostedDetail {
  tenantId: string;
  objectRef: ObjectRef;
  requestId: string;
  sourceVoucherId: string;
}

export function registerFinanceReversalPendingApproval(input: {
  tenantId: string;
  objectRef: ObjectRef;
  requestId: string;
  sourceVoucherId: string;
  actionLabel: string;
}): void {
  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
  });
  markWorkbenchFinanceReversalPending({
    tenantId: input.tenantId,
    objectRef: timelineRef,
    requestId: input.requestId,
    sourceVoucherId: input.sourceVoucherId,
    actionLabel: input.actionLabel,
    reversalRequestStatus: 'pending',
  });
  emitWorkbenchTimelineInvalidated(timelineRef);
}

export function resolveFinanceReversalPendingApproval(input: {
  tenantId: string;
  objectRef: ObjectRef;
}): ReturnType<typeof getWorkbenchFinanceReversalPending> {
  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
  });
  return getWorkbenchFinanceReversalPending(input.tenantId, timelineRef);
}

export async function postFinanceReversalAfterApprovalFromUi(input: {
  tenantId: string;
  objectRef: ObjectRef;
  requestId: string;
  sourceVoucherId: string;
}): Promise<boolean> {
  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
  });
  const pending = getWorkbenchFinanceReversalPending(input.tenantId, timelineRef);
  if (!pending || pending.requestId !== input.requestId) {
    showWorkbenchInfo('승인 대기 중인 역분개 요청을 찾을 수 없습니다.');
    return false;
  }

  await postFinanceReversalAfterApproval({
    tenantId: input.tenantId,
    requestId: input.requestId,
  });

  clearWorkbenchFinanceReversalPending(input.tenantId, timelineRef, input.requestId);
  emitWorkbenchTimelineInvalidated(timelineRef);
  showWorkbenchSuccess('승인 후 전표가 반영되었습니다.');

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<WorkbenchFinanceReversalPostedDetail>(
        WORKBENCH_FINANCE_REVERSAL_POSTED_EVENT,
        {
          detail: {
            tenantId: input.tenantId,
            objectRef: input.objectRef,
            requestId: input.requestId,
            sourceVoucherId: input.sourceVoucherId,
          },
        }
      )
    );
  }

  return true;
}
